import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { adminApi, authApi } from '../utils/api';
import { identifyUser, resetAnalytics, setRestaurantGroup, track, EVENTS } from '../utils/analytics';
import { canViewAs, readViewAs, viewAsTargetsFor, writeViewAs } from '../utils/viewAs';
import { clearTabTenant, setTabTenant } from '../utils/tabTenant';

import { useTenantId, useNavigate } from '../routing';
import { toUrlRid, parseTenantPath, tenantUrl } from '../utils/tenantPath';
import { suspendUnloadGuard } from '../utils/unloadGuard';

const AuthContext = createContext(null);
const EMPTY_RESTAURANTS = [];

// Pure mirror of server/lib/roles.js. Exported so the role matrix is testable
// without mounting the provider or involving localStorage/network effects.
export function roleCapabilities(role) {
  const isAdmin = role === 'admin';
  const isHqAdmin = role === 'hq_admin';
  const isManager = role === 'manager';
  const isViewer = role === 'viewer';
  const canEdit = isAdmin || isHqAdmin || isManager;
  return {
    isAdmin,
    isHqAdmin,
    isManager,
    isViewer,
    canEdit,
    canEditPaymentTerms: canEdit,
    canViewDiscounts: canEdit,
    canSeeHqOnly: isAdmin || isHqAdmin,
  };
}

// Name of the restaurant a user lands on, from the login/me payload (viewers
// carry it on `restaurant`; admins land with none until they pick one).
const landingNameOf = (userData, restaurantId) => (
  userData?.restaurant?.name
  || (Array.isArray(userData?.assigned_restaurants)
    ? userData.assigned_restaurants.find(r => String(r.id) === String(restaurantId))?.name
    : undefined)
);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
  });
  const restaurantId = useTenantId();
  const navigate = useNavigate();
  const [restaurantList, setRestaurantList] = useState(null);
  const [restaurantRefresh, setRestaurantRefresh] = useState(0);
  const listOwner = `${user?.id}:${user?.role}`;
  const currentList = restaurantList?.owner === listOwner ? restaurantList : null;
  const restaurants = user?.role === 'admin' ? (currentList?.items || EMPTY_RESTAURANTS) : (user?.assigned_restaurants || EMPTY_RESTAURANTS);
  const restaurantsLoaded = user?.role !== 'admin' || !!currentList?.loaded;
  const restaurantsError = !!currentList?.error;
  const [checkedSession, setCheckedSession] = useState(null);
  const [sessionError, setSessionError] = useState(false);
  const [deniedTenantId, setDeniedTenantId] = useState(null);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const refreshSession = useCallback(() => setRefreshVersion(v => v + 1), []);
  const sessionChecked = checkedSession?.token === token && checkedSession?.rid === restaurantId;
  const [viewAsRequest, setViewAsRequest] = useState(() => readViewAs());

  const login = useCallback((tokenVal, userData) => {
    localStorage.setItem('token', tokenVal);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(tokenVal);
    setUser(userData);
    identifyUser(userData, restaurantId, landingNameOf(userData, restaurantId));
    track(EVENTS.USER_LOGGED_IN, { role: userData?.role });
  }, [restaurantId]);

  // endedElsewhere: the session was ended by another tab (storage event), not
  // by this one. Then the shared token, user and default restaurant are left
  // alone: that tab already cleared them, and they are not this tab's to remove.
  // (The caller has confirmed the logout is still current — see the listener.)
  //
  // PostHog IS reset here, though without a second logout event: posthog-js
  // keeps the identity in memory and does not follow storage, so this tab would
  // otherwise file its login-screen pageview under the user who just left.
  const endSession = useCallback((endedElsewhere) => {
    if (endedElsewhere) {
      resetAnalytics();
      clearTabTenant();
    } else {
      track(EVENTS.USER_LOGGED_OUT);
      resetAnalytics();
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setTabTenant(null);
    }
    // Reset the state too, not only the storage: login → logout → login runs
    // without a reload, and a stale state would show the preview banner over
    // owner data (api.js reads storage, so no header would be sent).
    writeViewAs(null);
    setViewAsRequest(null);
    setToken(null);
    setUser(null);
    setCheckedSession(null);
    setDeniedTenantId(null);
    setRestaurantList(null);
  }, []);
  // No argument on purpose: logout is handed to onClick, which passes an event.
  const logout = useCallback(() => endSession(false), [endSession]);

  // Human-readable name for a restaurant id, from the loaded list (admin: global,
  // customer role: assigned). Used to enrich PostHog events with the restaurant name.
  const restaurantNameFor = useCallback(
    (id) => (id == null ? undefined : restaurants.find(r => String(r.id) === String(id))?.name),
    [restaurants],
  );

  const switchRestaurant = useCallback((id) => {
    if (toUrlRid(id) == null) return;
    const next = String(id);
    navigate(tenantUrl(next, '/suppliers'));
    const name = restaurantNameFor(next);
    setRestaurantGroup(next, name);
    track(EVENTS.RESTAURANT_SWITCHED, { restaurant_id: next, restaurant_name: name });
  }, [navigate, restaurantNameFor]);

  const lastSessionCheck = useRef(0);

  // Recheck on URL changes and focus. /auth/me itself checks membership:
  // a revoked restaurant can reject before an updated assignment list arrives.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    lastSessionCheck.current = Date.now();
    setSessionError(false);
    authApi.me().then(r => {
      if (cancelled || !r?.data) return;
      setUser(previous => JSON.stringify(previous) === JSON.stringify(r.data) ? previous : r.data);
      setDeniedTenantId(null);
      localStorage.setItem('user', JSON.stringify(r.data));
    }).catch(err => {
      if (cancelled) return;
      if (err?.response?.status === 401) logout();
      else if (err?.response?.status === 403 && err?.response?.data?.error === 'אין הרשאה למסעדה זו') {
        setDeniedTenantId(restaurantId);
      } else setSessionError(true);
    }).finally(() => {
      if (!cancelled) setCheckedSession({ token, rid: restaurantId });
    });
    return () => { cancelled = true; };
  }, [token, restaurantId, logout, refreshVersion]);

  useEffect(() => {
    const denied = (event) => {
      if (event.detail === restaurantId) setDeniedTenantId(event.detail);
    };
    const onFocus = () => {
      if (Date.now() - lastSessionCheck.current >= 30000) refreshSession();
    };
    window.addEventListener('peak:tenant-denied', denied);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('peak:tenant-denied', denied);
      window.removeEventListener('focus', onFocus);
    };
  }, [restaurantId, refreshSession]);

  // Who this tab is signed in as, readable from the storage listener below
  // without re-subscribing it on every user refresh.
  const userIdRef = useRef(user?.id);
  useEffect(() => { userIdRef.current = user?.id; }, [user]);

  // Cross-tab session sync. localStorage is shared across every tab of one
  // origin, so logging out in one tab already clears the token everywhere — but
  // the other tabs keep their in-memory `user` and go on rendering a signed-in
  // UI until their next request 401s. That was survivable when the only reason
  // to have two tabs open was two customer screens; the Command Center opens in
  // its own tab by design, so it is now the normal case.
  //
  // The `storage` event fires only in OTHER tabs, which is exactly what we want:
  // the tab that performed the action has already updated its own state.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== 'token') return;
      // Signed out elsewhere → drop this tab's session too. Unless somebody has
      // signed in again since: the event is stale, and the sign-in's own event
      // (handled below) is on its way.
      if (e.newValue == null) {
        if (localStorage.getItem('token') == null) endSession(true);
        return;
      }
      // Signed in elsewhere (or as somebody else) → adopt the new session
      // rather than keep issuing requests with a token we no longer hold.
      if (e.newValue !== localStorage.getItem('token')) return;
      let nextUser = null;
      try { nextUser = JSON.parse(localStorage.getItem('user')); } catch { /* keep the cached user */ }
      if (!nextUser) { setToken(e.newValue); return; }
      // Another identity must be checked against this tab's URL; never move
      // the tab to a different restaurant behind the person's back.
      if (nextUser.id !== userIdRef.current) {
        const tenant = parseTenantPath(window.location.pathname).rid;
        // Analytics FIRST, synchronously, before any state changes: React runs
        // child effects before this provider's own identify effect, so a child
        // keyed on restaurantId would otherwise fire its event under the
        // previous user's identity, which posthog-js still holds in memory.
        resetAnalytics();
        identifyUser(nextUser, tenant, landingNameOf(nextUser, tenant));
        // Keep the URL; TenantGate checks access under the new identity.
      }
      setToken(e.newValue);
      setUser(previous => JSON.stringify(previous) === JSON.stringify(nextUser) ? previous : nextUser);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [endSession]);

  // Re-establish PostHog identity after a page reload. login() identifies on
  // first sign-in, but a refresh restores the cached user without going through
  // login — so identify again whenever we have a user. identify is idempotent.
  useEffect(() => {
    if (user) identifyUser(user, restaurantId, restaurantNameFor(restaurantId));
  }, [user, restaurantId, restaurantNameFor]);

  const refreshRestaurants = useCallback(() => setRestaurantRefresh(v => v + 1), []);

  // A background refresh retains the last successful list. Identity/role changes
  // never reuse another user's list; /me object identity cannot duplicate this request.
  useEffect(() => {
    if (!token || user?.role !== 'admin') return;
    let cancelled = false;
    adminApi.getRestaurants().then(r => {
      if (!cancelled) setRestaurantList({ owner: listOwner, items: r.data || [], loaded: true, error: false });
    }).catch(() => {
      if (!cancelled) setRestaurantList(previous => ({
        ...(previous?.owner === listOwner ? previous : { items: [], loaded: false }),
        owner: listOwner, error: true,
      }));
    });
    return () => { cancelled = true; };
  }, [token, user?.id, user?.role, listOwner, refreshVersion, restaurantRefresh]);

  // Two roles, deliberately kept apart:
  //   realRole      — who the user IS. Drives tenancy only: which restaurants
  //                   load, whether the tenant chip can switch, ?rid= handoffs,
  //                   Command Center access. A preview never changes these.
  //   effectiveRole — who the user is LOOKING AS (read-only "view as"). Every
  //                   capability flag below derives from it, so a previewing
  //                   admin sees no admin control anywhere. Components must use
  //                   these flags, never `user.role` (guarded by a test).
  const realRole = user?.role;
  const viewAs = canViewAs(realRole, viewAsRequest) ? viewAsRequest : null;
  const effectiveRole = viewAs || realRole;
  const viewAsTargets = viewAsTargetsFor(realRole);
  // Switching re-loads the page so every screen refetches under the new role —
  // simpler and safer than teaching each page to drop its cached data.
  const setViewAs = useCallback((role) => {
    writeViewAs(canViewAs(realRole, role) ? role : null);
    suspendUnloadGuard();
    window.location.reload();
  }, [realRole]);

  // Role-derived capabilities — single source of truth mirrors server
  // lib/roles.js. admin = full; hq_admin and manager = operational edits
  // (credits, classifications, payment terms) + see discounts; viewer = read-only.
  const {
    isAdmin,
    isHqAdmin,
    isManager,
    isViewer,
    canEdit,
    canEditPaymentTerms,
    canViewDiscounts,
    canSeeHqOnly,
  } = roleCapabilities(effectiveRole);

  return (
    <AuthContext.Provider value={{ token, user, restaurantId, restaurants, login, logout, switchRestaurant, refreshRestaurants, restaurantsLoaded, restaurantsError, sessionChecked, sessionError, deniedTenantId, refreshSession, isAdmin, isHqAdmin, isManager, isViewer, canEdit, canEditPaymentTerms, canViewDiscounts, canSeeHqOnly, realRole, isRealAdmin: realRole === 'admin', viewAs, viewAsTargets, setViewAs }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
